import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';

const EarningsReport = ({ onClose }) => {
  const { user } = useAuth();
  const [earningsData, setEarningsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all_time');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClose = () => {
    document.body.style.overflow = 'unset';
    onClose();
  };

  useEffect(() => {
    loadEarningsData();
  }, [dateRange, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEarningsData = async () => {
    try {
      setLoading(true);
      
      let url = `${BACKEND_URL}/api/drivers/${user.id}/earnings`;
      const params = new URLSearchParams();
      
      // Add date parameters for all periods except all_time
      if (dateRange !== 'all_time') {
        if (startDate && endDate) {
          params.append('start_date', startDate);
          params.append('end_date', endDate);
        } else {
          // If dates aren't set yet, calculate them based on dateRange
          const now = new Date();
          let calculatedStart, calculatedEnd;
          
          if (dateRange === 'today') {
            calculatedStart = calculatedEnd = now.toISOString().split('T')[0];
          } else if (dateRange === 'this_week') {
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            calculatedStart = startOfWeek.toISOString().split('T')[0];
            calculatedEnd = now.toISOString().split('T')[0];
          } else if (dateRange === 'current_month') {
            calculatedStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            calculatedEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
          } else if (dateRange === 'last_month') {
            calculatedStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
            calculatedEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
          }
          
          if (calculatedStart && calculatedEnd) {
            params.append('start_date', calculatedStart);
            params.append('end_date', calculatedEnd);
            setStartDate(calculatedStart);
            setEndDate(calculatedEnd);
          }
        }
      }
      
      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setEarningsData(data);
      } else {
        console.error('Failed to load earnings data');
      }
    } catch (error) {
      console.error('Error loading earnings data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getCurrentMonthDates = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    };
  };

  const handleDateRangeChange = (e) => {
    const value = e.target.value;
    setDateRange(value);
    
    const now = new Date();
    
    if (value === 'today') {
      const today = now.toISOString().split('T')[0];
      setStartDate(today);
      setEndDate(today);
    } else if (value === 'this_week') {
      // Get start of week (Sunday)
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      setStartDate(startOfWeek.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (value === 'current_month') {
      const { start, end } = getCurrentMonthDates();
      setStartDate(start);
      setEndDate(end);
    } else if (value === 'last_month') {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    } else if (value === 'all_time') {
      setStartDate('');
      setEndDate('');
    }
  };

  if (loading) {
    return createPortal(
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content earnings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>📊 Earnings Report</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="loading-state">
              <p>Loading your earnings report...</p>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content earnings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📊 Earnings Report</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="modal-body">
          {/* Date Range Selector */}
          <div className="date-range-selector">
            <label htmlFor="date-range">Report Period:</label>
            <select 
              id="date-range" 
              value={dateRange} 
              onChange={handleDateRangeChange}
              className="form-select"
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="current_month">Current Month</option>
              <option value="last_month">Last Month</option>
              <option value="all_time">All Time</option>
            </select>
          </div>

          {earningsData && (
            <div className="earnings-summary-report">
              <div className="summary-cards">
                <div className="summary-card">
                  <h3>Total Earnings</h3>
                  <p className="amount">R{earningsData.summary.total_earnings}</p>
                </div>
                <div className="summary-card">
                  <h3>Total Trips</h3>
                  <p className="amount">{earningsData.summary.total_trips}</p>
                </div>
                <div className="summary-card">
                  <h3>Average Per Trip</h3>
                  <p className="amount">R{earningsData.summary.average_per_trip}</p>
                </div>
                <div className="summary-card">
                  <h3>Average Rating</h3>
                  <p className="amount">⭐ {earningsData.summary.average_rating}</p>
                </div>
              </div>

              <div className="current-month-highlight">
                <h3>This Month Performance</h3>
                <div className="month-stats">
                  <div className="stat">
                    <span className="stat-label">Earnings:</span>
                    <span className="stat-value">R{earningsData.current_month.earnings}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Trips:</span>
                    <span className="stat-value">{earningsData.current_month.trips}</span>
                  </div>
                </div>
              </div>

              <div className="trips-breakdown">
                <h3>Trip Details</h3>
                <div className="trips-table-container">
                  <table className="trips-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Passenger</th>
                        <th>Route</th>
                        <th>Distance</th>
                        <th>Duration</th>
                        <th>Earnings</th>
                        <th>Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earningsData.trips.map((trip, index) => (
                        <tr key={index}>
                          <td>{formatDate(trip.date)}</td>
                          <td>{trip.passenger}</td>
                          <td className="route-cell">{trip.route}</td>
                          <td>{trip.distance}km</td>
                          <td>{trip.duration}min</td>
                          <td className="earnings-cell">R{trip.earnings}</td>
                          <td className="rating-cell">
                            {trip.rating ? `⭐ ${trip.rating}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EarningsReport;